import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { CheckCircle, Warning } from '@phosphor-icons/react';

const AcceptInvite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [inviteDetails, setInviteDetails] = useState(null);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    fetchInviteDetails();
  }, [token]);

  const fetchInviteDetails = async () => {
    try {
      const response = await api.get(`/invite/${token}`);
      setInviteDetails(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Invalid or expired invite link');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    const userToken = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');

    if (!userToken || user.role !== 'startup') {
      toast.error('Please log in as a startup to accept this invite');
      navigate('/login', { state: { inviteToken: token } });
      return;
    }

    setAccepting(true);
    try {
      const response = await api.post(
        '/startup/accept-invite',
        { invite_token: token }
      );

      toast.success(`Connected to ${response.data.vc_fund_name}!`);
      navigate('/startup/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept invite');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-text-secondary">Loading invite...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="w-16 h-16 bg-danger/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Warning size={32} className="text-danger" weight="bold" />
          </div>
          <h2 className="text-2xl font-heading font-bold text-text-primary mb-3">Invalid Invite</h2>
          <p className="text-text-secondary mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={32} className="text-primary" weight="bold" />
          </div>
          <h2 className="text-3xl font-heading font-bold text-text-primary mb-3">
            You've been invited!
          </h2>
          <p className="text-lg text-text-secondary">
            {inviteDetails.vc_fund_name} has invited you to join Founderboard
          </p>
        </div>

        <div className="bg-surface border border-border rounded-[10px] p-6 mb-6">
          <div className="space-y-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-text-secondary mb-1">Invited To</div>
              <div className="font-heading font-bold text-xl text-text-primary">{inviteDetails.vc_fund_name}</div>
            </div>

            {inviteDetails.invited_email && (
              <div>
                <div className="text-xs uppercase tracking-wider text-text-secondary mb-1">Your Email</div>
                <div className="text-text-primary">{inviteDetails.invited_email}</div>
              </div>
            )}

            {inviteDetails.personal_note && inviteDetails.personal_note !== 'Shareable link' && (
              <div>
                <div className="text-xs uppercase tracking-wider text-text-secondary mb-1">Message from VC</div>
                <div className="text-text-primary bg-background border border-border rounded-lg p-3">
                  {inviteDetails.personal_note}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-info/10 border border-info/20 rounded-[10px] p-4 mb-6">
          <p className="text-sm text-info">
            By accepting this invite, {inviteDetails.vc_fund_name} will be able to view your monthly updates and track your progress.
          </p>
        </div>

        <button
          onClick={handleAccept}
          disabled={accepting}
          className="w-full bg-primary text-white py-3 rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="accept-invite-button"
        >
          {accepting ? 'Accepting...' : 'Accept Invite & Connect'}
        </button>

        <div className="text-center mt-4">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-text-secondary hover:text-primary transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
